import { useEffect, useRef, useState } from "react";

import { Psychology as BrainIcon, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * ReasoningDisplay component - displays streaming reasoning summary in a collapsible panel
 * Inspired by lobe-chat's Thinking component but without lobehub dependencies
 */
export const ReasoningDisplay = ({ reasoning, isStreaming = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);

  // Auto-scroll to bottom when content changes during streaming
  useEffect(() => {
    if (!isStreaming || !isExpanded || !contentRef.current) return;

    const container = contentRef.current;
    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceToBottom < 120;

    if (isNearBottom) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [reasoning, isStreaming, isExpanded]);

  if (!reasoning || reasoning.trim() === "") {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "background.paper",
        transition: "all 0.2s ease-out",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          minHeight: "32px",
          backgroundColor: isExpanded ? "action.hover" : "transparent",
          cursor: "pointer",
          transition: "background 0.2s ease-out",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BrainIcon
            sx={{
              fontSize: "1rem",
              color: isExpanded ? "primary.main" : "text.secondary",
            }}
          />
          {isStreaming ? (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: "0.75rem",
                background:
                  "linear-gradient(120deg, rgba(128, 188, 0, 0) 40%, rgba(128, 188, 0, 1) 50%, rgba(128, 188, 0, 0) 60%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "rgba(128, 188, 0, 0.45)",
                backgroundSize: "200% 100%",
                animation: "shine 1.5s linear infinite",
                "@keyframes shine": {
                  "0%": { backgroundPosition: "100%" },
                  "100%": { backgroundPosition: "-100%" },
                },
              }}
            >
              Thinking...
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: "0.75rem",
                color: isExpanded ? "text.primary" : "text.secondary",
              }}
            >
              Thought
            </Typography>
          )}
        </Box>
        <IconButton size="small" sx={{ padding: 0 }}>
          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded} timeout={200}>
        <Box
          ref={contentRef}
          onWheel={(e) => {
            // Prevent parent scroll when scrolling inside reasoning display
            const element = e.currentTarget;
            const isScrollable = element.scrollHeight > element.clientHeight;

            if (isScrollable) {
              const isAtTop = element.scrollTop === 0;
              const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

              // Prevent parent scroll unless we're at the boundary and scrolling in that direction
              if ((e.deltaY < 0 && !isAtTop) || (e.deltaY > 0 && !isAtBottom)) {
                e.stopPropagation();
              }
            }
          }}
          sx={{
            padding: "8px 12px",
            paddingBottom: "8px",
            maxHeight: "min(40vh, 320px)",
            overflowY: "auto",
            backgroundColor: "background.default",
            "& p": {
              margin: "0.5em 0",
              fontSize: "0.875rem",
              lineHeight: 1.7,
            },
            "& p:first-of-type": {
              marginTop: 0,
            },
            "& p:last-of-type": {
              marginBottom: 0,
            },
            "& ul, & ol": {
              marginLeft: "1.5em",
              marginTop: "0.5em",
              marginBottom: "0.5em",
            },
            "& li": {
              fontSize: "0.875rem",
              lineHeight: 1.7,
            },
            "& code": {
              backgroundColor: "action.hover",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "0.8125rem",
              fontFamily: "monospace",
            },
            "& pre": {
              backgroundColor: "action.hover",
              padding: "12px",
              borderRadius: "6px",
              overflow: "auto",
              marginTop: "0.5em",
              marginBottom: "0.5em",
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml={true}>
            {reasoning}
          </ReactMarkdown>
        </Box>
      </Collapse>
    </Box>
  );
};
